import { discoveryApi } from '@/api/clients/discoveryApi'
import type * as discovery from '@/api/types/discovery'
import type { DiscoveryFacts } from '@/api/types/readiness'
import type { CbomAsset, DiscoveryComponent, DiscoveryFinding, DiscoverySystem, MigrationFacts, VendorRoadmap } from '@/api/types/migration'

// ── Facts the migration module reads (I3 §12) ───────────────────────────────
// Systems are the Discovery inventories the readiness sync fetched from
// qtz-readiness (`/discovery/systems`; CII = Discovery's data classification,
// served as `cii`); CBOM assets
// come from `/discovery/assets`, qtz-readiness's copy of Module 1's crypto
// findings; vendor roadmaps from Feature 3. Waves are always empty: qtz-risk
// does not serve a wave plan and the feature is being withdrawn, so nothing is
// fetched for them.

export const EMPTY_MIGRATION_FACTS: MigrationFacts = { connected: false, scanner: 'Discovery scan orchestration (M1 F12)', systems: [], components: [], assets: [], findings: [], waves: [], wave_plan_updated_at: null, vendors: [], playbook_notes: [], fetched_at: new Date(0).toISOString() }

// Discovery's severity folded to the three levels the roadmap shows; a value
// it does not know (Discovery also reports `info`) reads as medium.
const SEVERITY: Record<string, DiscoveryFinding['severity']> = { critical: 'high', high: 'high', medium: 'medium', low: 'low' }
const SEVERITY_RANK: Record<DiscoveryFinding['severity'], number> = { high: 3, medium: 2, low: 1 }

function worstSeverity(levels: string[]): DiscoveryFinding['severity'] {
  return levels.map((l) => SEVERITY[l] ?? 'medium').reduce((acc, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[acc] ? s : acc), 'low')
}

function earliest(dates: Array<string | null | undefined>): string | null {
  const known = dates.filter((d): d is string => !!d).sort()
  return known[0] ?? null
}

// An asset found through a source that several systems share belongs, for the
// plan, to a prioritised (CII) one first: items and annexes are raised against
// the system CSA asks about. Assets whose source sits in no system are left out.
function systemOf(asset: discovery.Asset, prioritised: Set<string>): string | null {
  const ids = asset.system_ids ?? []
  return ids.find((id) => prioritised.has(id)) ?? ids[0] ?? null
}

interface PlacedAsset {
  asset: discovery.Asset
  systemId: string
}

const isVulnerable = (asset: discovery.Asset) => asset.quantum_risk === 'vulnerable' || asset.quantum_risk === 'partial'

function toCbomAsset({ asset: x, systemId }: PlacedAsset, vendorSourceIds: Set<string>): CbomAsset {
  const vulnerable = isVulnerable(x)
  return {
    id: x.id,
    code: x.id.slice(0, 8),
    system_id: systemId,
    component_id: `cmp:${systemId}`,
    detected_technology: x.name,
    name: x.name,
    model_version: x.cert_subject ?? x.protocol_version ?? x.name,
    asset_type: x.asset_type,
    service_provided: x.usage_context ?? x.source_name ?? '',
    protocols_algorithms: [x.name, x.key_size ? `${x.key_size}-bit` : null, x.protocol_version, x.primitive].filter(Boolean).join(' · '),
    package_type_vendor: x.source_name ?? '',
    // The asset's vendor is the vendor-supplied source (HSM, KMS, vault…) it was found through.
    vendor_id: vendorSourceIds.has(x.source_id) ? x.source_id : null,
    current_algorithm: x.name,
    quantum_vulnerable: vulnerable,
    // Discovery's latest scan is the scanner's answer: the asset carries the
    // vulnerable algorithm until Discovery stops reporting it.
    vulnerable_present: vulnerable,
    group_id: null,
    group_name: null,
  }
}

// Real organisation: systems from the Discovery inventories the readiness sync
// holds, assets from qtz-readiness's Discovery read model, vendors from the
// vendor components the readiness sync already found. A failed asset read
// degrades to no assets; the readiness facts decide "connected".
export async function fetchMigrationFacts(orgId: string, discovery: DiscoveryFacts | null): Promise<MigrationFacts> {
  if (!orgId) return EMPTY_MIGRATION_FACTS
  if (!discovery?.connected) return { ...EMPTY_MIGRATION_FACTS, fetched_at: new Date().toISOString() }

  const list: discovery.Asset[] = await discoveryApi.allAssets(orgId).catch(() => [])
  const now = new Date().toISOString()

  const systems: DiscoverySystem[] = discovery.inventories.map((inv) => ({
    id: inv.id,
    name: inv.name,
    description: inv.description,
    function_supported: inv.description,
    hosting: inv.environment ?? '',
    inventorisation_type: discovery.cbom_inventory_ids.includes(inv.id) ? 'Automated discovery · CBOM' : 'Manual register',
    cii: inv.cii ?? false,
    inventory_id: inv.id,
    business_unit: inv.business_unit,
    asset_count: inv.asset_count ?? 0,
    last_scan_at: inv.last_scan_at ?? null,
  }))
  // Until Discovery reports components, each system carries one default
  // component so items can still be created against system › component.
  const components: DiscoveryComponent[] = systems.map((sys) => ({ id: `cmp:${sys.id}`, system_id: sys.id, name: 'Default component', description: 'Assigned until Discovery reports components' }))
  const prioritised = new Set(discovery.inventories.filter((i) => i.prioritised).map((i) => i.id))
  const vendorSourceIds = new Set(discovery.vendor_components.map((v) => v.id))
  const placed: PlacedAsset[] = list
    .map((asset) => ({ asset, systemId: systemOf(asset, prioritised) }))
    .filter((p): p is PlacedAsset => p.systemId !== null)
  const assets: CbomAsset[] = placed.map((p) => toCbomAsset(p, vendorSourceIds))
  // One finding per (system, algorithm) among vulnerable assets: the severity
  // is the worst Discovery reported, found_at the earliest sighting.
  const byKey = placed
    .filter((p) => isVulnerable(p.asset))
    .reduce((m, p) => m.set(`${p.systemId}|${p.asset.name}`, [...(m.get(`${p.systemId}|${p.asset.name}`) ?? []), p]), new Map<string, PlacedAsset[]>())
  const findings: DiscoveryFinding[] = Array.from(byKey.entries()).map(([k, ps]) => ({
    id: `fnd:${k}`,
    system_id: ps[0].systemId,
    asset_ids: ps.map((p) => p.asset.id),
    algorithm: ps[0].asset.name,
    title: `${ps[0].asset.name} on ${ps.length} ${ps.length === 1 ? 'asset' : 'assets'}`,
    severity: worstSeverity(ps.map((p) => p.asset.severity)),
    found_at: earliest(ps.map((p) => p.asset.discovered_at ?? p.asset.ingested_at)) ?? now,
  }))
  const vendors: VendorRoadmap[] = discovery.vendor_components.map((v) => ({ vendor_id: v.id, name: v.name, roadmap: null, risk_flags: [] }))
  return { connected: true, scanner: 'Discovery scan orchestration (M1 F12)', systems, components, assets, findings, waves: [], wave_plan_updated_at: null, vendors, playbook_notes: [], fetched_at: now }
}
